import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Megaphone, FileText, Printer } from "lucide-react";
import { api, fmtDate, gbp, EDITOR_LINKEDIN } from "@/lib/api";
import { ArticleMeta, AccessBadge, SponsorBadge, ArticleCard } from "@/components/ArticleCard";
import { Takeaways } from "@/components/Takeaways";
import { SponsorSlot } from "@/components/SponsorSlot";
import { TipJar } from "@/components/TipJar";
import { Paywall } from "@/components/Paywall";
import { Body } from "@/components/ArticleBody";

const Sources = ({ items }) => (
  <div className="mt-10 max-w-[68ch] border-t-2 border-[#0A0A0A] pt-5" data-testid="article-sources">
    <p className="overline text-[#575757]">Sources</p>
    <ol className="mt-3 space-y-2 text-sm">
      {items.map((s, i) => (
        <li key={i} className="flex gap-3">
          <span className="font-mono-d text-[#E5332A]">{String(i + 1).padStart(2, "0")}</span>
          <a href={s.url} target="_blank" rel="noopener noreferrer" className="hover:text-[#E5332A] underline-offset-2 hover:underline" data-testid={`article-source-${i}`}>
            {s.title} <span className="text-[#575757]">· {s.source}{s.published ? ` · ${fmtDate(s.published)}` : ""}</span>
          </a>
        </li>
      ))}
    </ol>
  </div>
);

const ReportUpsell = ({ report }) =>
  report ? (
    <Link to={`/research/${report.slug}`} data-testid="article-report-upsell" className="group flex gap-4 border border-[#E7E7E7] bg-white p-5 hover:border-[#E5332A] transition-colors">
      <FileText className="text-[#E5332A] shrink-0" size={22} />
      <div>
        <p className="overline text-[#E5332A] !text-[9px]">Go deeper · Sector report</p>
        <p className="font-display font-semibold mt-1 group-hover:text-[#E5332A]">{report.title}</p>
        <p className="text-xs text-[#575757] mt-1">{report.pages} pages · {gbp(report.price)}</p>
      </div>
    </Link>
  ) : null;

const SponsorThis = ({ slug }) => (
  <Link to={`/sponsor?article=${slug}`} data-testid="sponsor-this-article-link" className="flex gap-4 border border-dashed border-[#DADADA] bg-[#F6F6F6]/40 p-5 hover:bg-[#F6F6F6] transition-colors">
    <Megaphone className="text-[#E5332A] shrink-0" size={20} />
    <div>
      <p className="font-display font-semibold">Sponsor this article</p>
      <p className="text-xs text-[#575757] mt-1">Put your brand in front of trade advisors, buyers and investors reading this story.</p>
    </div>
  </Link>
);

export default function ArticlePage() {
  const { slug } = useParams();
  const [a, setA] = useState(null);
  const [reports, setReports] = useState([]);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setA(null); setErr(false); window.scrollTo(0, 0);
    api.get(`/articles/${slug}`).then((r) => setA(r.data)).catch(() => setErr(true));
    api.get("/reports").then((r) => setReports(r.data));
  }, [slug]);

  if (err) return <div className="max-w-3xl mx-auto px-5 py-24 font-display text-3xl">Story not found.</div>;
  if (!a) return <div className="min-h-[70vh]" />;
  const report = reports.find((r) => r.category === a.category) || reports[0];

  return (
    <article data-testid="article-page">
      <header className="max-w-4xl mx-auto px-5 lg:px-10 pt-12 lg:pt-16">
        <div className="flex flex-wrap items-center gap-3"><ArticleMeta a={a} /><AccessBadge access={a.access} /></div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mt-5" data-testid="article-title">{a.title}</h1>
        <p className="text-base md:text-lg text-[#575757] mt-6 leading-relaxed">{a.excerpt}</p>
        <div className="flex flex-wrap items-center justify-between gap-4 mt-8 pb-6 border-b border-[#E7E7E7]">
          <div className="text-sm">{a.author === "Wilbert Frank Chaniwa" ? <a href={EDITOR_LINKEDIN} target="_blank" rel="noopener noreferrer" data-testid="author-linkedin-link" className="font-medium hover:text-[#E5332A] underline-offset-2 hover:underline">{a.author}</a> : <span className="font-medium">{a.author}</span>}<span className="text-[#575757]"> · {a.author_role}</span>
            <p className="text-xs text-[#575757] mt-1">{fmtDate(a.published_at)} · {a.read_time} min read</p></div>
          <div className="flex items-center gap-3">
            {a.sponsor && <SponsorBadge name={a.sponsor.name} label={`${a.sponsor.tier_label}:`} />}
            <button onClick={() => window.print()} data-testid="article-print-btn" className="no-print text-[#575757] hover:text-[#E5332A]"><Printer size={16} /></button>
          </div>
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-5 lg:px-10 mt-10">
        <div className="img-zoom overflow-hidden aspect-[21/9] bg-[#E7E7E7]"><img src={a.image} alt={a.title} className="w-full h-full object-cover" /></div>
      </div>
      <div className="max-w-7xl mx-auto px-5 lg:px-10 mt-12 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8 lg:pl-[8%] min-w-0">
          <Takeaways items={a.takeaways} locked={a.locked} />
          <Body paras={a.body} />
          {!a.locked && a.sources?.length > 0 && <Sources items={a.sources} />}
          {a.locked ? <Paywall access={a.access} /> : (
            <div className="mt-12 grid sm:grid-cols-2 gap-4 no-print">
              <ReportUpsell report={report} />
            </div>
          )}
          <div className="mt-12 no-print"><TipJar articleSlug={a.slug} /></div>
        </div>
        <aside className="lg:col-span-4 space-y-6 no-print">
          <div className="lg:sticky lg:top-36 space-y-6">
            {a.sponsor ? <SponsorSlot sponsor={a.sponsor} articleSlug={a.slug} /> : <SponsorThis slug={a.slug} />}
            {a.locked && <ReportUpsell report={report} />}
          </div>
        </aside>
      </div>
      {a.related?.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 lg:px-10 mt-24 no-print">
          <h2 className="font-display text-2xl sm:text-3xl font-bold border-b border-[#E7E7E7] pb-4 mb-10">More on {a.category}</h2>
          <div className="grid md:grid-cols-3 gap-8">{a.related.map((r, i) => <ArticleCard key={r.slug} a={r} index={i} />)}</div>
        </section>
      )}
    </article>
  );
}
